import { useState } from "react";
import { Trash2, ListTodo } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import TimesheetHourCell from "./TimesheetHourCell";
import type { TimeEntry, Timesheet } from "@/lib/backend";
import type { Task } from "@/lib/types";

interface TimesheetEntryRowProps {
  entry: TimeEntry;
  task: Task | null;
  weekDates: string[]; // 7 YYYY-MM-DD strings for the week
  allEntries: TimeEntry[]; // all entries for this row's (project, task/description) line
  timesheetId: string;
  timesheetStatus: Timesheet["status"];
  onSaveHours: (entryDate: string, hours: number, existingEntryId: string | null) => Promise<void>;
  onDelete: () => void;
  onTaskClick: (task: Task) => void;
}

function taskStatusColor(task: Task): string {
  if (!task.due_date) return "bg-white/30";
  const due = new Date(task.due_date).getTime();
  const now = Date.now();
  const diff = due - now;
  const threeDays = 3 * 24 * 60 * 60 * 1000;
  if (task.status === "complete") return "bg-green-400";
  if (diff < 0) return "bg-red-500";
  if (diff < threeDays) return "bg-yellow-400";
  return "bg-green-400";
}

export default function TimesheetEntryRow({
  entry,
  task,
  weekDates,
  allEntries,
  timesheetStatus,
  onSaveHours,
  onDelete,
  onTaskClick,
}: TimesheetEntryRowProps) {
  const readOnly = timesheetStatus !== "draft";
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      onDelete();
    } finally {
      setDeleting(false);
    }
  };

  const totalHours = allEntries.reduce((sum, e) => sum + Number(e.hours), 0);

  return (
    <tr className="border-b border-white/5 hover:bg-white/[0.02] group">
      {/* Activity column */}
      <td className="pl-8 pr-2 py-1.5 text-xs">
        {task ? (
          <button
            className="inline-flex items-center gap-1 max-w-[220px] group/pill"
            onClick={() => onTaskClick(task)}
          >
            <Badge
              variant="outline"
              className="cursor-pointer gap-1 border-primary/45 bg-primary/12 px-2 py-0.5 text-xs text-primary hover:bg-primary/20 transition-colors"
            >
              <ListTodo className="w-3 h-3 shrink-0" />
              <span className="truncate max-w-[140px]">{task.title}</span>
            </Badge>
            {/* Due date proximity bar */}
            <span className={`w-1.5 h-4 rounded-full shrink-0 ${taskStatusColor(task)}`} />
          </button>
        ) : (
          <span className="text-white/60 truncate block max-w-[220px]" title={entry.description ?? ""}>
            {entry.description}
          </span>
        )}
      </td>

      {/* Hour cells — one per day of the week */}
      {weekDates.map((dateKey) => {
        const dayEntry = allEntries.find((e) => e.entry_date === dateKey);
        return (
          <TimesheetHourCell
            key={dateKey}
            hours={dayEntry ? Number(dayEntry.hours) : null}
            readOnly={readOnly}
            onSave={(hours) => onSaveHours(dateKey, hours, dayEntry?.id ?? null)}
          />
        );
      })}

      {/* Row total */}
      <td className="px-2 py-1 text-center text-xs font-medium text-white/70">
        {totalHours > 0 ? (totalHours % 1 === 0 ? totalHours : totalHours.toFixed(2)) : <span className="text-white/20">—</span>}
      </td>

      {/* Delete */}
      <td className="px-1 py-1 text-center">
        {!readOnly && (
          <button
            className="opacity-0 group-hover:opacity-100 transition-opacity text-white/30 hover:text-red-400"
            onClick={handleDelete}
            disabled={deleting}
            aria-label="Delete entry"
          >
            <Trash2 className="w-3 h-3" />
          </button>
        )}
      </td>
    </tr>
  );
}
